/**
 * @fileoverview A program's email settings — which emails it sends, and to whom.
 *
 * Lives on the Settings tab beside the listing details rather than in the
 * overview: the overview answers "how is this program going", and a switch you
 * set once is not that.
 *
 * Every gate here is off until somebody turns it on, and the recipient list
 * shows the org's administrators as fixed entries so nobody wonders why they
 * received one. Each administrator can still switch program emails off for
 * themselves (Settings › Preferences), which the line under the list says.
 */

import { useEffect, useState } from "react";
import { toast } from "sonner";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { Card, SectionHeader, ListRow, Tag, Button } from "@/components/studio/primitives";

function Toggle({ on, onClick, testId, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-pressed={on}
      aria-label={label}
      className={`relative shrink-0 rounded-full transition-colors ${on ? "bg-[var(--accent-yellow)]" : "bg-[var(--studio-interactive)]"}`}
      style={{ width: 40, height: 22 }}
    >
      <span
        className="absolute top-0.5 rounded-full bg-[var(--studio-knob)] shadow-sm transition-transform"
        style={{ width: 18, height: 18, left: 2, transform: on ? "translateX(18px)" : "translateX(0)" }}
      />
    </button>
  );
}

const inputClass =
  "min-w-0 flex-1 rounded-xl border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-4 py-3 " +
  "font-sans text-[14px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-quaternary)] " +
  "focus:border-[var(--accent-yellow)]";

export default function ProgramEmailSettings({ program: fromProps, admins = [], onChanged }) {
  const [newEmail, setNewEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [program, setProgram] = useState(fromProps);

  useEffect(() => setProgram(fromProps), [fromProps]);

  const added = program.notification_recipients || [];
  const recipients = [
    ...admins.map((a) => ({
      email: a.email,
      name: [a.first_name, a.last_name].filter(Boolean).join(" ") || null,
      source: "org_admin",
    })),
    ...added.map((email) => ({ email, name: null, source: "added" })),
  ];

  /** Optimistic patch; rolls back if the save fails. */
  const patch = async (fields, failure) => {
    const before = program;
    setProgram((p) => ({ ...p, ...fields }));
    setSaving(true);
    try {
      const res = await orgApi.put(`/org/programs/${program.program_id}`, fields);
      const saved = res.data?.program || { ...before, ...fields };
      setProgram(saved);
      onChanged?.(saved);
    } catch (err) {
      setProgram(before);
      toast.error(getOrgErrorMessage(err, failure));
    } finally {
      setSaving(false);
    }
  };

  const toggle = (key) => patch({ [key]: !program[key] }, "Couldn't change that setting");

  const addRecipient = (e) => {
    e?.preventDefault?.();
    const email = newEmail.trim().toLowerCase();
    if (!email) return;
    if (recipients.some((r) => r.email.toLowerCase() === email)) {
      toast.error("That address already receives these emails");
      return;
    }
    setNewEmail("");
    patch({ notification_recipients: [...added, email] }, "Couldn't add that address");
  };

  const removeRecipient = (email) =>
    patch({ notification_recipients: added.filter((e) => e !== email) }, "Couldn't remove that address");

  return (
    <div data-testid="program-email-settings">
      <SectionHeader>Program emails</SectionHeader>
      <Card className="mb-3">
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <div className="font-sans text-[15px] text-[var(--text-primary)]">Weekly recap</div>
              <div className="mt-0.5 font-sans text-[13px] text-[var(--text-tertiary)]">
                What was taught last week, totalled and broken out by instructor. Sent on the day and time set on the Notifications page.
              </div>
            </div>
            <Toggle on={!!program.weekly_recap_enabled} onClick={() => toggle("weekly_recap_enabled")} testId="toggle-weekly-recap" label="Weekly recap" />
          </div>
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <div className="font-sans text-[15px] text-[var(--text-primary)]">Week ahead</div>
              <div className="mt-0.5 font-sans text-[13px] text-[var(--text-tertiary)]">
                Sessions scheduled for the coming week, by instructor. Sent on the day and time set on the Notifications page.
              </div>
            </div>
            <Toggle on={!!program.week_ahead_enabled} onClick={() => toggle("week_ahead_enabled")} testId="toggle-week-ahead" label="Week ahead" />
          </div>
          <div className="flex items-center justify-between gap-4 border-t border-[var(--studio-divider)] pt-4">
            <div className="min-w-0">
              <div className="font-sans text-[15px] text-[var(--text-primary)]">Tell families about cancellations</div>
              <div className="mt-0.5 font-sans text-[13px] text-[var(--text-tertiary)]">
                Off for a sponsored class, where the sponsoring organization tells its own roster. On for a
                program whose families book directly.
              </div>
            </div>
            <Toggle
              on={!!program.cancellation_notifications}
              onClick={() => toggle("cancellation_notifications")}
              testId="toggle-cancellation-notifications"
              label="Tell families about cancellations"
            />
          </div>
        </div>
      </Card>

      <Card padding={0} className="mb-2">
        {recipients.map((r, i) => (
          <ListRow
            key={r.email}
            borderBottom={i < recipients.length - 1}
            title={r.name || r.email}
            subtitle={r.name ? r.email : undefined}
            trailing={
              r.source === "org_admin" ? (
                <Tag>Org admin</Tag>
              ) : (
                <button
                  type="button"
                  onClick={() => removeRecipient(r.email)}
                  className="font-sans text-[13px] text-[var(--state-danger)] hover:underline"
                >
                  Remove
                </button>
              )
            }
          />
        ))}
      </Card>
      <p className="mb-3 font-sans text-[13px] text-[var(--text-tertiary)]" data-testid="program-email-admin-note">
        Admins can turn program emails off for themselves in Settings &rsaquo; Preferences.
      </p>
      <form onSubmit={addRecipient} className="mb-6 flex gap-2">
        <input
          type="email"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          placeholder="Add an address"
          aria-label="Add a recipient address"
          className={inputClass}
        />
        <Button type="submit" disabled={saving}>Add</Button>
      </form>
    </div>
  );
}
